import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import moment from 'moment/moment'
import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import join from 'lodash/join'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import round from 'lodash/round'

import {
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@material-ui/core'

import { useT } from '@galo-monitor/i18n'
import {
  checked as checkedIcon,
  plot as plotIcon
} from '@galo-monitor/icons'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import { selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import colors from '@galo-monitor/styles/colors'
import fonts from '@galo-monitor/styles/fonts'
import { momentFriendlyDateFormat, momentFriendlyDateUSFormat } from '@galo-monitor/utils/dates'
import Card from '@galo-monitor/web-components/Card'
import Icon from '@galo-monitor/web-components/Icon'
import CircularLoader from '@galo-monitor/web-components/Loader/CircularLoader'

import { useActivities } from '../../context/Activities'
import useStyles from './styles'
import {
  CardFooter,
  SubheaderContainer,
  TextBold
} from './theme'

const PlotListSelector = props => {
  const {
    harvestData,
    loading
  } = props
  const classes = useStyles()
  const t = useT()

  const { selected } = useSelector(selectLanguage)
  const { initials } = useSelector(selectUnitOfMeasurement)

  const { currentPlotIndex, currentHarvest, changePlot } = useActivities()

  const plotsTotalArea = useMemo(
    () => harvestData?.plots
      ? reduce(
        harvestData.plots,
        (total, { convertedArea }) => total + convertedArea,
        0
      )
      : 0,
    [harvestData]
  )

  return (
    <Card
      title={ t('plot', { howMany: 2 }) }
      style={ {
        marginTop: 0,
        marginBottom: 0
      } }
      cardStyle={ {
        width: '100%',
        position: 'relative',
        paddingBottom: 0
      } }
      headerTitleProps={ {
        style: {
          paddingLeft: 20,
          fontSize: fonts.fontSize.S,
          fontWeight: fonts.fontWeight.bold
        }
      } }
    >
      {currentHarvest ? (
        <SubheaderContainer>
          <Chip
            className={ join([classes.chip, classes.success, classes.fontWhite], ' ') }
            label={ currentHarvest.label }
          />
          {
            Number(currentHarvest.value) > 0 && harvestData?.harvest
              ? (
                <>
                  <Typography variant="body2">
                    {`${ t('culture', { howMany: 1 }) }: ${ harvestData.harvest.cultivation }`}
                  </Typography>
                  <Typography variant="body2">
                    {`${ t('period', { howMany: 1 }) }: ${
                      t('from {start} to {finish}', {
                        start: moment(harvestData.harvest.dateInitial)
                          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat),
                        finish: moment(harvestData.harvest.dateFinish)
                          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
                      })
                    }`}
                  </Typography>
                </>
              ) : null
          }
        </SubheaderContainer>
      ) : null}
      <List>
        {loading ? <CircularLoader className={ classes.loader } /> : null}
        {isEmpty(harvestData.plots)
          ? (
            <>
              <Divider style={ { margin: 0 } } />
              <ListItem className={ join([classes.item, classes.emptyMessage], ' ') }>
                <ListItemText primary={ t('datagrid body empty data source message') } />
              </ListItem>
            </>
          ) : (
            map(harvestData.plots, ({ plot, convertedArea }, index) => {
              const isSelected = currentPlotIndex === index
              return (
                <React.Fragment key={ plot.id }>
                  <Divider style={ { margin: 0 } } />
                  <ListItem
                    className={
                      loading
                        ? join([
                          classes.item,
                          classes.loadingItems
                        ], ' ')
                        : join([
                          classes.item,
                          ...(isSelected ? [classes.selectedItem] : [])
                        ],' ')
                    }
                    onClick={ () => changePlot(plot.id, index) }
                  >
                    <ListItemIcon classes={ { root: classes.listItemIcon } }>
                      <Icon icon={ plotIcon } color={ colors.darkGrey } />
                    </ListItemIcon>
                    <ListItemText
                      primary={ plot.name }
                      secondary={ `${ convertedArea } ${ initials }` }
                    />
                    {isSelected ? (
                      <ListItemSecondaryAction>
                        <Icon
                          icon={ checkedIcon }
                          color={ colors.primary }
                          size={ 18 }
                        />
                      </ListItemSecondaryAction>
                    ) : null}
                  </ListItem>
                </React.Fragment>
              )
            }))}
        <Divider style={ { margin: 0 } } />
        <CardFooter>
          <TextBold variant="body1">
            {t('total')}
          </TextBold>
          <TextBold color="textSecondary" variant="body1">
            {`${ round(plotsTotalArea, 2) } ${ initials }`}
          </TextBold>
        </CardFooter>
      </List>
    </Card>
  )
}

PlotListSelector.defaultProps = {
  harvestData: {
    harvest: {},
    plots: []
  }
}

PlotListSelector.propTypes = {
  harvestData: PropTypes.shape({
    harvest: PropTypes.object,
    plots: PropTypes.array
  }),
  loading: PropTypes.bool.isRequired
}

export default PlotListSelector
